import { useCallback } from "react";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { useRosettaConfig } from "~/lib/app/hooks";

import { FallbackPricing } from "./consts";
import type { BillingCycle, Bundle, Variant } from "./types";

export const usePricing = () => {
  const { rosettaConfigState } = useRosettaConfig();
  return { pricing: rosettaConfigState.result?.posties?.pricing ?? FallbackPricing };
};

export const useTracking = (variant: Variant) => {
  const triggerPoint = variant === "brochure" ? "Posties brochure" : "Posties paywall";

  const trackBillingCycleToggle = useCallback(
    (cycle: BillingCycle) => {
      sendGATracking({
        action: `Subscribe to SCMP/${triggerPoint}/Plan term toggle/Click`,
        category: "Subscribe to SCMP",
        label: cycle === "annual" ? "Yearly" : "Monthly",
      });
    },
    [triggerPoint],
  );

  const trackBundleSelect = useCallback(
    (bundle: Bundle, cycle: BillingCycle) => {
      const pianoTermNames: Record<`${Bundle}|${BillingCycle}`, string> = {
        "epaper-bundle|annual": "Annual: Posties ePaper + Digital",
        "epaper-bundle|monthly": "Monthly: Posties ePaper + Digital",
        "print-bundle|annual": "Annual: Posties Print + Digital",
        "print-bundle|monthly": "Monthly: Posties Print + Digital",
      };
      const label = pianoTermNames[`${bundle}|${cycle}`];

      sendGATracking({
        action: `Subscribe to SCMP/${triggerPoint}/Plans/Click`,
        category: "Subscribe to SCMP",
        label,
      });
    },
    [triggerPoint],
  );

  const trackCompareBenefitClick = useCallback(() => {
    sendGATracking({
      action: `Subscribe to SCMP/${triggerPoint}/Compare benefits/Click`,
      category: "Subscribe to SCMP",
      label: "",
    });
  }, [triggerPoint]);

  const trackSubscribeClick = useCallback(() => {
    sendGATracking({
      action: `Subscribe to SCMP/${triggerPoint}/Subscribe/Click`,
      category: "Subscribe to SCMP",
      label: "",
    });
  }, [triggerPoint]);

  const trackRedeemClick = useCallback(() => {
    sendGATracking({
      action: `Subscribe to SCMP/${triggerPoint}/Redeem/Click`,
      category: "Subscribe to SCMP",
      label: "",
    });
  }, [triggerPoint]);

  return {
    trackBillingCycleToggle,
    trackBundleSelect,
    trackCompareBenefitClick,
    trackRedeemClick,
    trackSubscribeClick,
  };
};
